import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';

export const translations = {
    en: {
        title: 'Admin Dashboard',
        users: 'Users',
        search: 'Search by name or email',
        refresh: 'Refresh',
        loading: 'Loading...',
        noUsers: 'No users found',
        never: 'Never',
        metadataNote: 'Last sign-in records authentication. Last activity updates at most once per hour while CourtClic is being used.',
        columns: {
            email: 'Account',
            plan: 'Plan',
            status: 'Status',
            created: 'Created',
            lastLogin: 'Last sign-in',
            lastActive: 'Last activity',
            actions: 'Actions'
        },
        grantStandard: 'Give Premium VIP',
        grantPro: 'Give Pro VIP',
        grantDialog: {
            duration: 'Duration (months)',
            confirm: 'Grant access',
            cancel: 'Cancel',
            standardTitle: 'Give Premium VIP Access',
            proTitle: 'Give Pro VIP Access'
        },
        grantSuccess: 'VIP access granted.',
        error: 'An error occurred.',
        unauthorized: 'Unauthorized access',
        revokeVip: 'Revoke VIP',
        revokeDialog: {
            title: 'Revoke VIP access',
            message: 'This immediately returns the account to the free plan. Continue?',
            confirm: 'Revoke access',
            cancel: 'Keep access'
        },
        revokeSuccess: 'VIP access revoked.',
        deleteAccount: 'Delete app record',
        deleteDialog: {
            title: 'Delete application record',
            message: 'This deletes the CourtClic profile, preferences, and favourites for {email}.',
            warning: 'The Firebase Authentication account is not deleted. Remove it manually in Firebase Console after this step. Active paid subscriptions must be cancelled first.',
            confirm: 'Delete app record',
            cancel: 'Cancel'
        },
        deleteSuccess: 'Application record deleted. The Authentication account still needs to be removed manually.',
        admin: 'Admin',
        disabled: 'Disabled'
    },
    fr: {
        title: 'Tableau de bord admin',
        users: 'Utilisateurs',
        search: 'Rechercher par nom ou courriel',
        refresh: 'Actualiser',
        loading: 'Chargement...',
        noUsers: 'Aucun utilisateur trouvé',
        never: 'Jamais',
        metadataNote: 'La dernière connexion correspond à l’authentification. La dernière activité est mise à jour au maximum une fois par heure pendant l’utilisation de CourtClic.',
        columns: {
            email: 'Compte',
            plan: 'Forfait',
            status: 'Statut',
            created: 'Créé',
            lastLogin: 'Dernière connexion',
            lastActive: 'Dernière activité',
            actions: 'Actions'
        },
        grantStandard: 'Donner VIP Premium',
        grantPro: 'Donner VIP Pro',
        grantDialog: {
            duration: 'Durée (mois)',
            confirm: 'Accorder l’accès',
            cancel: 'Annuler',
            standardTitle: 'Donner accès VIP Premium',
            proTitle: 'Donner accès VIP Pro'
        },
        grantSuccess: 'Accès VIP accordé.',
        error: 'Une erreur est survenue.',
        unauthorized: 'Accès non autorisé',
        revokeVip: 'Révoquer VIP',
        revokeDialog: {
            title: 'Révoquer l’accès VIP',
            message: 'Le compte retournera immédiatement au forfait gratuit. Continuer?',
            confirm: 'Révoquer l’accès',
            cancel: 'Garder l’accès'
        },
        revokeSuccess: 'Accès VIP révoqué.',
        deleteAccount: 'Supprimer les données',
        deleteDialog: {
            title: 'Supprimer les données CourtClic',
            message: 'Le profil CourtClic, les préférences et les favoris de {email} seront supprimés.',
            warning: 'Le compte Firebase Authentication ne sera pas supprimé. Supprimez-le manuellement dans la console Firebase après cette étape. Tout abonnement payant actif doit d’abord être annulé.',
            confirm: 'Supprimer les données',
            cancel: 'Annuler'
        },
        deleteSuccess: 'Données CourtClic supprimées. Le compte Authentication doit encore être supprimé manuellement.',
        admin: 'Admin',
        disabled: 'Désactivé'
    }
};

const StyledContainer = styled(Container)(({ theme }) => ({
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
}));

const SearchBar = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
        alignItems: 'stretch',
        flexDirection: 'column'
    }
}));

export const formatAdminDate = (value, isFrench, neverLabel = '—') => {
    if (!value) return neverLabel;
    const date = typeof value.toDate === 'function'
        ? value.toDate()
        : new Date(value);
    if (Number.isNaN(date.getTime())) return neverLabel;

    return new Intl.DateTimeFormat(isFrench ? 'fr-CA' : 'en-CA', {
        dateStyle: 'medium',
        timeStyle: 'short'
    }).format(date);
};

const getAdminDateValue = (value) => {
    if (!value) return null;
    const date = typeof value.toDate === 'function'
        ? value.toDate()
        : new Date(value);
    const timestamp = date.getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
};

export const getAdminSortValue = (user, column) => {
    switch (column) {
        case 'account':
            return (user.displayName || user.email || '').trim().toLocaleLowerCase();
        case 'plan':
            return (user.accountType || 'free').toLocaleLowerCase();
        case 'status':
            return user.disabled
                ? 'disabled'
                : (user.subscriptionStatus || '').toLocaleLowerCase();
        case 'created':
            return getAdminDateValue(user.createdAt);
        case 'lastLogin':
            return getAdminDateValue(user.lastLoginAt);
        case 'lastActive':
            return getAdminDateValue(user.lastActiveAt);
        default:
            return '';
    }
};

export const sortAdminUsers = (users, column, direction) => users
    .map((user, originalIndex) => ({ user, originalIndex }))
    .sort((leftEntry, rightEntry) => {
        const left = getAdminSortValue(leftEntry.user, column);
        const right = getAdminSortValue(rightEntry.user, column);
        const leftMissing = left === null || left === '';
        const rightMissing = right === null || right === '';

        // Keep unknown metadata at the bottom in both directions. This makes
        // the useful records stay visible while older users are backfilled.
        if (leftMissing !== rightMissing) return leftMissing ? 1 : -1;
        if (leftMissing && rightMissing) {
            return leftEntry.originalIndex - rightEntry.originalIndex;
        }

        const comparison = typeof left === 'number'
            ? left - right
            : left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });

        if (comparison === 0) {
            return leftEntry.originalIndex - rightEntry.originalIndex;
        }
        return direction === 'asc' ? comparison : -comparison;
    })
    .map(({ user }) => user);

export const getNextAdminSort = (currentSort, column) => ({
    column,
    direction: currentSort.column === column && currentSort.direction === 'asc'
        ? 'desc'
        : 'asc'
});

export default function AdminPage({ isFrench }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [checkingAdmin, setCheckingAdmin] = useState(true);
    const [notice, setNotice] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [grantDialogOpen, setGrantDialogOpen] = useState(false);
    const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [duration, setDuration] = useState(12);
    const [isAdmin, setIsAdmin] = useState(false);
    const [selectedPlanType, setSelectedPlanType] = useState('standard');
    const [actionLoading, setActionLoading] = useState('');
    const [sort, setSort] = useState({ column: 'lastActive', direction: 'desc' });

    const t = isFrench ? translations.fr : translations.en;
    const currentUserId = firebase.auth().currentUser?.uid;

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const snapshot = await firebase.firestore()
                .collection('users')
                .get();
            setUsers(snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data()
            })));
        } catch (error) {
            console.error('Error fetching users:', error);
            setNotice({
                severity: 'error',
                message: error.message || t.error
            });
        } finally {
            setLoading(false);
        }
    }, [t.error]);

    useEffect(() => {
        const checkAdminStatus = async () => {
            const user = firebase.auth().currentUser;
            if (!user) {
                setCheckingAdmin(false);
                setLoading(false);
                return;
            }

            try {
                const snapshot = await firebase.firestore()
                    .collection('users')
                    .doc(user.uid)
                    .get();
                const authorized = snapshot.exists && snapshot.data().isAdmin === true;
                setIsAdmin(authorized);
                if (authorized) await fetchUsers();
            } catch (error) {
                setNotice({ severity: 'error', message: error.message || t.error });
                setLoading(false);
            } finally {
                setCheckingAdmin(false);
            }
        };

        checkAdminStatus();
    }, [fetchUsers, t.error]);

    const handleGrantVIP = async () => {
        if (!selectedUser) return;
        const actionKey = `grant:${selectedUser.id}`;
        setActionLoading(actionKey);

        try {
            const endDate = new Date();
            endDate.setMonth(endDate.getMonth() + duration);

            await firebase.firestore()
                .collection('users')
                .doc(selectedUser.id)
                .update({
                    accountType: selectedPlanType,
                    subscriptionStatus: 'active',
                    subscriptionEndDate: endDate,
                    subscriptionPeriodEnd: endDate,
                    subscriptionCancelAtPeriodEnd: false,
                    subscriptionType: 'admin_granted',
                    isAdminGranted: true
                });

            setNotice({ severity: 'success', message: t.grantSuccess });
            await fetchUsers();
        } catch (error) {
            setNotice({ severity: 'error', message: error.message || t.error });
        } finally {
            setActionLoading('');
            setGrantDialogOpen(false);
            setSelectedUser(null);
        }
    };

    const handleRevokeVIP = async () => {
        if (!selectedUser) return;
        setActionLoading(`revoke:${selectedUser.id}`);

        try {
            const deleteField = firebase.firestore.FieldValue.delete();
            await firebase.firestore()
                .collection('users')
                .doc(selectedUser.id)
                .update({
                    accountType: 'free',
                    subscriptionStatus: 'inactive',
                    isAdminGranted: false,
                    subscriptionType: deleteField,
                    subscriptionEndDate: deleteField,
                    subscriptionPeriodEnd: deleteField,
                    subscriptionCancelAtPeriodEnd: deleteField
                });
            setNotice({ severity: 'success', message: t.revokeSuccess });
            await fetchUsers();
        } catch (error) {
            setNotice({ severity: 'error', message: error.message || t.error });
        } finally {
            setActionLoading('');
            setRevokeDialogOpen(false);
            setSelectedUser(null);
        }
    };

    const handleDeleteAccount = async () => {
        if (!selectedUser) return;
        setActionLoading(`delete:${selectedUser.id}`);

        try {
            const db = firebase.firestore();
            const batch = db.batch();
            batch.delete(db.collection('users').doc(selectedUser.id));
            batch.delete(db.collection('userPreferences').doc(selectedUser.id));
            batch.delete(db.collection('userFavorites').doc(selectedUser.id));
            await batch.commit();
            setNotice({ severity: 'success', message: t.deleteSuccess });
            await fetchUsers();
        } catch (error) {
            setNotice({ severity: 'error', message: error.message || t.error });
        } finally {
            setActionLoading('');
            setDeleteDialogOpen(false);
            setSelectedUser(null);
        }
    };

    const filteredUsers = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLocaleLowerCase();
        const matchingUsers = users.filter((user) =>
            user.email?.toLocaleLowerCase().includes(normalizedSearch) ||
            user.displayName?.toLocaleLowerCase().includes(normalizedSearch)
        );
        return sortAdminUsers(matchingUsers, sort.column, sort.direction);
    }, [searchTerm, sort, users]);

    const handleSort = (column) => {
        setSort((currentSort) => getNextAdminSort(currentSort, column));
    };

    const sortableColumns = [
        { column: 'account', label: t.columns.email },
        { column: 'plan', label: t.columns.plan },
        { column: 'status', label: t.columns.status },
        { column: 'created', label: t.columns.created },
        { column: 'lastLogin', label: t.columns.lastLogin },
        { column: 'lastActive', label: t.columns.lastActive }
    ];

    if (checkingAdmin) {
        return (
            <StyledContainer className="admin-view">
                <CircularProgress aria-label={t.loading} />
            </StyledContainer>
        );
    }

    if (!isAdmin) {
        return (
            <StyledContainer className="admin-view">
                <Alert severity="error">{t.unauthorized}</Alert>
            </StyledContainer>
        );
    }

    return (
        <StyledContainer className="admin-view" maxWidth="xl">
            <Typography variant="h4" gutterBottom>
                {t.title}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
                {users.length} {t.users.toLowerCase()}
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
                {t.metadataNote}
            </Alert>

            <SearchBar>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={t.search}
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
                    }}
                />
                <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={fetchUsers}
                    disabled={loading}
                >
                    {t.refresh}
                </Button>
            </SearchBar>

            {loading ? (
                <CircularProgress aria-label={t.loading} />
            ) : (
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 1220 }}>
                        <TableHead>
                            <TableRow>
                                {sortableColumns.map(({ column, label }) => (
                                    <TableCell
                                        key={column}
                                        sortDirection={sort.column === column ? sort.direction : false}
                                    >
                                        <TableSortLabel
                                            active={sort.column === column}
                                            direction={sort.column === column ? sort.direction : 'asc'}
                                            onClick={() => handleSort(column)}
                                        >
                                            {label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell>{t.columns.actions}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredUsers.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        {t.noUsers}
                                    </TableCell>
                                </TableRow>
                            )}
                            {filteredUsers.map((user) => {
                                const hasActivePaidSubscription = (
                                    user.subscriptionStatus === 'active' &&
                                    user.isAdminGranted !== true
                                );
                                const cannotDelete = (
                                    user.id === currentUserId ||
                                    user.isAdmin === true ||
                                    hasActivePaidSubscription
                                );

                                return (
                                    <TableRow key={user.id} hover>
                                        <TableCell sx={{ minWidth: 220 }}>
                                            <Typography variant="body2" fontWeight={700}>
                                                {user.displayName || user.email || '—'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {user.email || '—'}
                                            </Typography>
                                            {user.isAdmin && (
                                                <Chip label={t.admin} size="small" sx={{ ml: 1 }} />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={user.accountType || 'free'}
                                                color={
                                                    user.accountType === 'pro'
                                                        ? 'secondary'
                                                        : user.accountType === 'standard'
                                                            ? 'primary'
                                                            : 'default'
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                {user.subscriptionStatus && (
                                                    <Chip
                                                        label={user.subscriptionStatus}
                                                        color={user.subscriptionStatus === 'active' ? 'success' : 'error'}
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                )}
                                                {user.disabled && (
                                                    <Chip label={t.disabled} color="error" size="small" />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 155 }}>
                                            {formatAdminDate(user.createdAt, isFrench, t.never)}
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 155 }}>
                                            {formatAdminDate(user.lastLoginAt, isFrench, t.never)}
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 155 }}>
                                            {formatAdminDate(user.lastActiveAt, isFrench, t.never)}
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 300 }}>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {user.isAdminGranted ? (
                                                    <Button
                                                        variant="outlined"
                                                        color="warning"
                                                        disabled={Boolean(actionLoading)}
                                                        onClick={() => {
                                                            setSelectedUser(user);
                                                            setRevokeDialogOpen(true);
                                                        }}
                                                    >
                                                        {t.revokeVip}
                                                    </Button>
                                                ) : !user.isAdmin && (
                                                    <>
                                                        <Button
                                                            variant="outlined"
                                                            disabled={Boolean(actionLoading)}
                                                            onClick={() => {
                                                                setSelectedUser(user);
                                                                setSelectedPlanType('standard');
                                                                setGrantDialogOpen(true);
                                                            }}
                                                        >
                                                            {t.grantStandard}
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            disabled={Boolean(actionLoading)}
                                                            onClick={() => {
                                                                setSelectedUser(user);
                                                                setSelectedPlanType('pro');
                                                                setGrantDialogOpen(true);
                                                            }}
                                                        >
                                                            {t.grantPro}
                                                        </Button>
                                                    </>
                                                )}
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={<DeleteForeverOutlinedIcon />}
                                                    disabled={cannotDelete || Boolean(actionLoading)}
                                                    title={hasActivePaidSubscription
                                                        ? t.deleteDialog.warning
                                                        : undefined}
                                                    onClick={() => {
                                                        setSelectedUser(user);
                                                        setDeleteDialogOpen(true);
                                                    }}
                                                >
                                                    {t.deleteAccount}
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={grantDialogOpen} onClose={() => setGrantDialogOpen(false)}>
                <DialogTitle>
                    {selectedPlanType === 'pro'
                        ? t.grantDialog.proTitle
                        : t.grantDialog.standardTitle}
                </DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>{t.grantDialog.duration}</InputLabel>
                        <Select
                            value={duration}
                            label={t.grantDialog.duration}
                            onChange={(event) => setDuration(event.target.value)}
                        >
                            {[1, 3, 6, 12, 24].map((months) => (
                                <MenuItem key={months} value={months}>{months}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGrantDialogOpen(false)}>
                        {t.grantDialog.cancel}
                    </Button>
                    <Button
                        onClick={handleGrantVIP}
                        variant="contained"
                        disabled={Boolean(actionLoading)}
                    >
                        {t.grantDialog.confirm}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={revokeDialogOpen} onClose={() => setRevokeDialogOpen(false)}>
                <DialogTitle>{t.revokeDialog.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{t.revokeDialog.message}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRevokeDialogOpen(false)}>
                        {t.revokeDialog.cancel}
                    </Button>
                    <Button
                        onClick={handleRevokeVIP}
                        variant="contained"
                        color="warning"
                        disabled={Boolean(actionLoading)}
                    >
                        {t.revokeDialog.confirm}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>{t.deleteDialog.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t.deleteDialog.message.replace(
                            '{email}',
                            selectedUser?.email || selectedUser?.displayName || '—'
                        )}
                    </DialogContentText>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        {t.deleteDialog.warning}
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        {t.deleteDialog.cancel}
                    </Button>
                    <Button
                        onClick={handleDeleteAccount}
                        variant="contained"
                        color="error"
                        disabled={Boolean(actionLoading)}
                    >
                        {t.deleteDialog.confirm}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={Boolean(notice)}
                autoHideDuration={6000}
                onClose={() => setNotice(null)}
            >
                <Alert
                    onClose={() => setNotice(null)}
                    severity={notice?.severity || 'error'}
                >
                    {notice?.message || t.error}
                </Alert>
            </Snackbar>
        </StyledContainer>
    );
}
